import { useEffect, useRef, useState } from 'react';

interface UseCountUpOpts {
  /** Total animation duration in ms. Defaults to 700ms — feels snappy, not laggy. */
  durationMs?: number;
  /** Decimals to preserve when interpolating. Defaults to 0. */
  decimals?: number;
  /** Skip the animation entirely (e.g. when reduced-motion is on). */
  immediate?: boolean;
}

/**
 * Smoothly count from the previous value (or 0) to `target` over the given
 * duration with an ease-out cubic curve. Returns the live displayed number.
 *
 * Used to give the Today/Reports hero numbers the Apple Health / Stripe
 * dashboard feel — numbers that "land" instead of pop. Free dopamine when
 * a number going up is the point.
 *
 * Respects prefers-reduced-motion automatically.
 */
export function useCountUp(target: number, opts: UseCountUpOpts = {}): number {
  const { durationMs = 700, decimals = 0, immediate = false } = opts;
  const [value, setValue] = useState<number>(target);
  const previousRef = useRef<number>(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Honor reduced motion / immediate mode by snapping straight to target.
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || reduce || !Number.isFinite(target)) {
      setValue(target);
      previousRef.current = target;
      return;
    }

    const start = previousRef.current;
    const delta = target - start;
    if (delta === 0) {
      setValue(target);
      return;
    }
    const startTs = performance.now();

    const factor = 10 ** decimals;
    const step = (now: number) => {
      const t = Math.min(1, (now - startTs) / durationMs);
      // ease-out cubic — fast start, gentle settle.
      const eased = 1 - Math.pow(1 - t, 3);
      const current = start + delta * eased;
      setValue(Math.round(current * factor) / factor);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        previousRef.current = target;
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs, decimals, immediate]);

  return value;
}
